import { Label } from "@/components/ui/label"
import {
  Select as ShadSelect,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { useEffect, useId, useRef, useState } from "react"

export type SelectProps = {
  className?: string
  selectClassName?: string
  label?: string
  placeholder?: string
  disabled?: boolean
  options: SelectOption[]
  value?: string
  showValueInSelected?: boolean
  onChange?: (value: string, option: SelectOption) => void
}

export type SelectOption = {
  label: string
  value: string
}

export default function Select({
  className,
  selectClassName,
  label,
  placeholder = "Select an option",
  options,
  value,
  showValueInSelected,
  disabled,
  onChange,
}: SelectProps) {
  const triggerBtn = useRef<HTMLButtonElement>(null)
  const [dropdownWidth, setDropdownWidth] = useState("")
  const id = useId()

  const [localValue, setLocalValue] = useState<string>(value || "")
  const [selectedLabel, setSelectedLabel] = useState("")

  function handleChange(value: string) {
    setLocalValue(value)
    const label = getLabelFromValue(value)
    setSelectedLabel(label)

    if (onChange) {
      onChange(value, { label: label, value: value })
    }
  }

  function getLabelFromValue(value: string) {
    return options.find((item) => item.value === value)?.label || ""
  }

  useEffect(() => {
    if (triggerBtn.current) {
      setDropdownWidth(`${triggerBtn.current.getBoundingClientRect().width}px`)
    }
  }, [])

  useEffect(() => {
    setLocalValue(value || "")
    const newLabel = getLabelFromValue(value || "")
    setSelectedLabel(newLabel)
  }, [value, options])

  return (
    <div className={cn("text-sm flex flex-col justify-between", className)}>
      {label && (
        <Label htmlFor={id} className="mb-2 font-medium">
          {label}
        </Label>
      )}

      <ShadSelect
        value={localValue}
        disabled={disabled}
        onValueChange={handleChange}
      >
        <SelectTrigger
          id={id}
          className={cn(
            "h-12 w-full bg-white hover:cursor-pointer",
            showValueInSelected && "h-15",
            selectClassName
          )}
        >
          <SelectValue className="px-4" placeholder={placeholder}>
            {showValueInSelected ? (
              <div className="text-left">
                <div className="font-medium">{localValue}</div>
                <div className="text-label-text text-xs font-medium">
                  {selectedLabel}
                </div>
              </div>
            ) : (
              selectedLabel
            )}
          </SelectValue>
        </SelectTrigger>

        <SelectContent style={{ width: dropdownWidth }}>
          <SelectGroup>
            {options.map((option) => (
              <SelectItem key={option.value} value={`${option.value}`}>
                {option.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </ShadSelect>
    </div>
  )
}
